import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { toast } from 'sonner';
import { probeAgent, agentInfo, agentKnownOnline, printViaAgent, columnsTestText, inlineQrCodes } from '../lib/printAgent';
import { Building2, Save, Palette, Sun, Moon, Printer, Banknote, FileSignature, Database, Download, Upload, AlertTriangle, RefreshCw, CheckCircle2, Image as ImageIcon, Trash2, Scale, Percent, FileArchive } from 'lucide-react';
import { FiscalXmlExportPanel } from '../components/FiscalXmlExportPanel';
import { AccountantEmailPanel } from '../components/AccountantEmailPanel';
import { FiscalHomologationTools } from '../components/FiscalHomologationTools';
import { refreshCompanySettings, loadCompanySettings } from '../hooks/useCompanySettings';
import { useProductCategories } from '../lib/categories';
import { SettingsNFCeTab, SettingsPixTab } from '../components/SettingsExtensions';
import { FiscalSettingsPanel } from '../components/FiscalSettingsPanel';
import { useUpdateStatus } from '../hooks/useUpdateStatus';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COMPANY_FIELDS = [
  { name: 'trade_name', label: 'Nome Fantasia *', required: true, placeholder: 'Ex: Marge Hortifruti', cols: 2 },
  { name: 'legal_name', label: 'Razão Social', placeholder: 'Ex: Marge Hortifruti LTDA ME', cols: 2 },
  { name: 'cnpj', label: 'CNPJ', placeholder: '13.721.992/0001-98' },
  { name: 'ie', label: 'Inscrição Estadual (IE)', placeholder: '28368052-0' },
  { name: 'phone', label: 'Telefone', placeholder: '67991444584' },
  { name: 'email', label: 'E-mail', placeholder: 'contato@empresa.com' },
  { name: 'address', label: 'Endereço (rua + número)', placeholder: 'Av Dom Pedro II, 2386', cols: 2 },
  { name: 'neighborhood', label: 'Bairro', placeholder: 'Vila Planalto' },
  { name: 'zip_code', label: 'CEP', placeholder: '79940-000' },
  { name: 'city', label: 'Cidade', placeholder: 'Caarapó' },
  { name: 'state', label: 'UF', placeholder: 'MS' },
  { name: 'receipt_footer', label: 'Mensagem no Rodapé do Cupom', placeholder: 'Volte sempre!', cols: 2 },
];

export const SettingsPage = () => {
  const [data, setData] = useState({});
  const productCategories = useProductCategories();
  const [netTest, setNetTest] = useState({ busy: false, ok: false, msg: '' });
  const testNetworkPrinter = async () => {
    setNetTest({ busy: true, ok: false, msg: '' });
    try {
      const r = await axios.post(`${API}/print/escpos/test`, { text: '', ip: data.printer_ip, port: Number(data.printer_port) || 9100 }, { withCredentials: true, timeout: 12000 });
      setNetTest({ busy: false, ok: true, msg: `✓ Enviado para ${r.data.ip}:${r.data.port}` });
    } catch (e) {
      setNetTest({ busy: false, ok: false, msg: e.response?.data?.detail || e.message });
    }
  };
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [tab, setTab] = useState('empresa');
  const { theme, changeTheme } = useTheme();

  useEffect(() => {
    axios
      .get(`${API}/settings/company`, { withCredentials: true })
      .then((r) => setData(r.data || {}))
      .catch(() => setError('Não foi possível carregar as configurações'));
  }, []);

  const set = (k, v) => setData({ ...data, [k]: v });

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!data.trade_name || !data.trade_name.trim()) {
      setError('Nome Fantasia é obrigatório');
      return;
    }
    setLoading(true); setError(''); setSuccess('');
    try {
      const { id, nfce_certificate_present, nfce_certificate_filename, nfce_certificate_subject, nfce_certificate_uploaded_at, nfce_certificate_expires_at, logo_base64, ...payload } = data;
      const r = await axios.put(`${API}/settings/company`, payload, { withCredentials: true });
      setData(r.data);
      await refreshCompanySettings();
      setSuccess('Configurações salvas com sucesso!');
      toast.success('Configurações salvas', { description: 'Impressora, cupom e demais ajustes já valem no PDV.' });
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao salvar');
      toast.error('Erro ao salvar', { description: err.response?.data?.detail || err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="p-8 max-w-5xl" data-testid="settings-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-md bg-blue-600 flex items-center justify-center">
            <Building2 size={24} className="text-white" />
          </div>
          <div>
            <h1 className="text-4xl font-black text-slate-900 tracking-tight">Configurações</h1>
            <p className="text-sm text-slate-600">Empresa, PIX, NFCe, Tema e Impressora</p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full max-w-[1200px] mb-6">
            <TabsTrigger value="empresa" data-testid="tab-empresa"><Building2 size={14} className="mr-1" /> Empresa</TabsTrigger>
            <TabsTrigger value="pix" data-testid="tab-pix"><Banknote size={14} className="mr-1" /> PIX</TabsTrigger>
            <TabsTrigger value="nfce" data-testid="tab-nfce"><FileSignature size={14} className="mr-1" /> Fiscal (NFC-e / NF-e)</TabsTrigger>
            <TabsTrigger value="balanca" data-testid="tab-balanca"><Scale size={14} className="mr-1" /> Balança</TabsTrigger>
            <TabsTrigger value="margens" data-testid="tab-margens"><Percent size={14} className="mr-1" /> Margens</TabsTrigger>
            <TabsTrigger value="tema" data-testid="tab-tema"><Palette size={14} className="mr-1" /> Tema</TabsTrigger>
            <TabsTrigger value="impressora" data-testid="tab-impressora"><Printer size={14} className="mr-1" /> Impressora</TabsTrigger>
            <TabsTrigger value="backup" data-testid="tab-backup"><Database size={14} className="mr-1" /> Backup</TabsTrigger>
            <TabsTrigger value="xml" data-testid="tab-xml"><FileArchive size={14} className="mr-1" /> XML Contador</TabsTrigger>
          </TabsList>

          {/* EMPRESA */}
          <TabsContent value="empresa">
            <CompanyLogoField
              logo={data.logo_base64}
              onChanged={(newData) => setData(newData)}
              setError={setError}
              setSuccess={setSuccess}
            />
            <form onSubmit={handleSave} className="bg-white rounded-md border border-slate-200 p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {COMPANY_FIELDS.map((f) => (
                <div key={f.name} className={`space-y-1 ${f.cols === 2 ? 'md:col-span-2' : ''}`}>
                  <Label>{f.label}</Label>
                  <Input
                    value={data[f.name] || ''}
                    onChange={(e) => set(f.name, e.target.value)}
                    placeholder={f.placeholder}
                    required={f.required}
                    data-testid={`settings-field-${f.name}`}
                  />
                </div>
              ))}
              <div className="space-y-1 md:col-span-2">
                <Label>Fuso Horário da Loja</Label>
                <select
                  value={data.timezone || 'America/Campo_Grande'}
                  onChange={(e) => set('timezone', e.target.value)}
                  className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="settings-field-timezone"
                >
                  <option value="America/Campo_Grande">Campo Grande / MS (UTC-4)</option>
                  <option value="America/Cuiaba">Cuiabá / MT (UTC-4)</option>
                  <option value="America/Manaus">Manaus / AM (UTC-4)</option>
                  <option value="America/Sao_Paulo">Brasília / SP-RJ-MG (UTC-3)</option>
                  <option value="America/Bahia">Salvador / BA (UTC-3)</option>
                  <option value="America/Fortaleza">Fortaleza / CE-PA (UTC-3)</option>
                  <option value="America/Recife">Recife / PE (UTC-3)</option>
                  <option value="America/Belem">Belém / PA (UTC-3)</option>
                  <option value="America/Porto_Velho">Porto Velho / RO-AC (UTC-4)</option>
                  <option value="America/Rio_Branco">Rio Branco / AC (UTC-5)</option>
                  <option value="America/Noronha">Fernando de Noronha (UTC-2)</option>
                </select>
                <p className="text-[11px] text-slate-400">Usado no horário das vendas, no dashboard e nos relatórios "de hoje".</p>
              </div>
              <div className="md:col-span-2 flex justify-end">
                <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="settings-save-empresa">
                  <Save size={18} className="mr-2" /> {loading ? 'Salvando...' : 'Salvar Empresa'}
                </Button>
              </div>
            </form>
          </TabsContent>

          {/* PIX */}
          <TabsContent value="pix">
            <SettingsPixTab settings={data} onChange={setData} />
            <div className="flex justify-end mt-4">
              <Button onClick={handleSave} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="settings-save-pix">
                <Save size={18} className="mr-2" /> {loading ? 'Salvando...' : 'Salvar PIX'}
              </Button>
            </div>
          </TabsContent>

          {/* NFCe */}
          <TabsContent value="nfce">
            <FiscalSettingsPanel settings={data} onChange={setData} onSaved={() => handleSave()} />
            <div className="h-6" />
            <FiscalHomologationTools ambiente={data.nfce_ambiente || 'homologacao'} />
            <div className="h-6" />
            <SettingsNFCeTab settings={data} onChange={setData} onSaveCallback={() => axios.get(`${API}/settings/company`, { withCredentials: true }).then(r => setData(r.data))} />
            <div className="flex justify-end mt-4">
              <Button onClick={handleSave} disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="settings-save-nfce">
                <Save size={18} className="mr-2" /> {loading ? 'Salvando...' : 'Salvar Fiscal'}
              </Button>
            </div>
          </TabsContent>

          {/* BALANÇA */}
          <TabsContent value="balanca">
            <div className="bg-white rounded-md border border-slate-200 p-6 space-y-5" data-testid="scale-settings">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0"><Scale size={20} /></div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Etiqueta de Balança</h3>
                  <p className="text-sm text-slate-500">Leitura automática de códigos de barras de balança (peso ou preço embutido). Ao ler no PDV, o produto é lançado com o valor já calculado.</p>
                </div>
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={data.scale_enabled !== false}
                  onChange={(e) => set('scale_enabled', e.target.checked)}
                  className="h-5 w-5 rounded border-slate-300 text-blue-600"
                  data-testid="scale-enabled"
                />
                <span className="text-sm font-medium text-slate-700">Ativar leitura de etiqueta de balança no PDV</span>
              </label>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Prefixo do código</Label>
                  <Input
                    value={data.scale_prefix ?? '2'}
                    onChange={(e) => set('scale_prefix', e.target.value.replace(/\D/g, '').slice(0, 2))}
                    placeholder="2"
                    data-testid="scale-prefix"
                  />
                  <p className="text-xs text-slate-400 mt-1">Normalmente "2" (padrão das balanças no Brasil).</p>
                </div>
                <div>
                  <Label>Tipo de etiqueta</Label>
                  <select
                    value={data.scale_mode || 'weight'}
                    onChange={(e) => set('scale_mode', e.target.value)}
                    className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
                    data-testid="scale-mode"
                  >
                    <option value="weight">Peso — produto vendido por kg</option>
                    <option value="price">Preço — valor total na etiqueta</option>
                  </select>
                  <p className="text-xs text-slate-400 mt-1">Peso: multiplica o preço/kg pelo peso. Preço: usa o valor da etiqueta.</p>
                </div>
                <div>
                  <Label>Dígitos do código do produto</Label>
                  <select
                    value={String(data.scale_code_len || 6)}
                    onChange={(e) => set('scale_code_len', parseInt(e.target.value, 10))}
                    className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
                    data-testid="scale-code-len"
                  >
                    <option value="6">6 dígitos</option>
                    <option value="5">5 dígitos</option>
                  </select>
                  <p className="text-xs text-slate-400 mt-1">Quantos dígitos após o prefixo formam o código do produto.</p>
                </div>
              </div>

              <Alert className="bg-slate-50 border-slate-200 text-slate-600">
                <AlertDescription className="text-xs leading-relaxed">
                  <b>Como funciona:</b> um código de 13 dígitos como <code className="font-mono">{(data.scale_prefix ?? '2')}{'0'.repeat(Math.max(0,(data.scale_code_len||6)-2))}21{data.scale_mode === 'price' ? '00990' : '01500'}0</code> é lido como
                  produto <b>#21</b> {data.scale_mode === 'price' ? 'com preço de R$ 9,90' : 'pesando 1,500 kg'}.
                  Cadastre o produto usando esse código no campo <b>"Código"</b> (ou código de barras).
                </AlertDescription>
              </Alert>

              <div className="flex justify-end">
                <Button onClick={handleSave} disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="settings-save-scale">
                  <Save size={18} className="mr-2" /> {loading ? 'Salvando...' : 'Salvar Balança'}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* TEMA */}
          <TabsContent value="margens">
            <div className="bg-white rounded-md border border-slate-200 p-6 space-y-5" data-testid="margin-settings">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0"><Percent size={20} /></div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Margem padrão por categoria</h3>
                  <p className="text-sm text-slate-500">Percentual sobre o custo. Ao lançar uma nota de entrada, o sistema sugere o preço de venda = custo × (1 + margem). Deixe em branco para não sugerir.</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {productCategories.map((c) => {
                  const v = data.category_margins?.[c.value];
                  return (
                    <div key={c.value} className="space-y-1">
                      <Label className="text-xs">{c.label}</Label>
                      <div className="relative">
                        <Input type="number" step="1" min="0" max="1000" value={v ?? ''} placeholder="ex: 30"
                          onChange={(e) => { const m = { ...(data.category_margins || {}) }; if (e.target.value === '') delete m[c.value]; else m[c.value] = parseFloat(e.target.value); set('category_margins', m); }}
                          className="pr-8" data-testid={`margin-${c.value}`} />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
                      </div>
                      {v > 0 && <p className="text-[11px] text-slate-500">Custo R$ 10,00 → venda R$ {(10 * (1 + v / 100)).toFixed(2)}</p>}
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-end">
                <Button onClick={handleSave} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="settings-save-margins">
                  <Save size={18} className="mr-2" /> {loading ? 'Salvando...' : 'Salvar Margens'}
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="tema">
            <div className="bg-white rounded-md border border-slate-200 p-6" data-testid="theme-settings">
              <h2 className="text-xl font-bold text-slate-900 mb-3">Tema do Sistema</h2>
              <p className="text-xs text-slate-600 mb-4">A escolha será aplicada em toda a aplicação (PDV, sidebar, telas)</p>
              <div className="grid grid-cols-2 gap-3 max-w-md">
                <button onClick={() => changeTheme('claro')} data-testid="settings-theme-claro" className={`flex items-center gap-3 p-4 rounded-md border-2 transition-all ${theme === 'claro' ? 'border-blue-600 bg-blue-50 ring-2 ring-blue-200' : 'border-slate-200 bg-white hover:bg-slate-50'}`}>
                  <Sun size={24} className="text-yellow-500" />
                  <div className="text-left">
                    <p className="font-bold text-slate-900">Claro</p>
                    <p className="text-xs text-slate-500">Padrão · branco</p>
                  </div>
                  {theme === 'claro' && <span className="ml-auto text-xs font-bold text-blue-600">ATIVO</span>}
                </button>
                <button onClick={() => changeTheme('azul')} data-testid="settings-theme-azul" className={`flex items-center gap-3 p-4 rounded-md border-2 transition-all ${theme === 'azul' ? 'border-blue-600 bg-blue-50 ring-2 ring-blue-200' : 'border-slate-200 bg-white hover:bg-slate-50'}`}>
                  <Moon size={24} className="text-blue-700" />
                  <div className="text-left">
                    <p className="font-bold text-slate-900">Azul Escuro Profissional</p>
                    <p className="text-xs text-slate-500">Navy #0a0f1e · bordas neon · destaques ciano</p>
                  </div>
                  {theme === 'azul' && <span className="ml-auto text-xs font-bold text-blue-600">ATIVO</span>}
                </button>
              </div>
            </div>
          </TabsContent>

          {/* IMPRESSORA */}
          <TabsContent value="impressora">
            <div className="space-y-4">
              {/* CABEÇALHO DO CUPOM — Editor de Texto Livre */}
              <div className="bg-white rounded-md border-2 border-blue-300 p-5">
                <h2 className="text-xl font-bold text-slate-900 mb-2 flex items-center gap-2">
                  <Printer size={20} className="text-blue-600" /> Cabeçalho do Cupom
                </h2>
                <p className="text-sm text-slate-600 mb-3">
                  Digite EXATAMENTE como você quer que apareça no topo do cupom (fiscal e não-fiscal).
                  A primeira linha vira o título em destaque, demais linhas vão centralizadas e menores.
                  <strong className="block mt-1 text-blue-800">Deixe em branco para usar os dados da aba Empresa automaticamente.</strong>
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-bold">Cabeçalho personalizado</Label>
                    <textarea
                      className="w-full h-44 p-3 rounded-md border-2 border-slate-300 font-mono text-sm focus:border-blue-500 focus:outline-none"
                      value={data.receipt_header || ''}
                      onChange={(e) => set('receipt_header', e.target.value)}
                      placeholder={`MARGE HORTIFRUTI LTDA ME\ncnpj: 13.721.992/0001-98  IE: 28368052-0\nend: Av Dom Pedro II, 2386\nbairro: Vila Planalto  f: 67991444584\ncidade: Caarapó-MS  cep: 79940-000`}
                      data-testid="settings-receipt-header"
                    />
                    <div className="flex gap-2 mt-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const c = data;
                          const lines = [];
                          if (c.trade_name) lines.push(c.trade_name);
                          if (c.cnpj || c.ie) lines.push(`${c.cnpj ? `cnpj: ${c.cnpj}` : ''}${c.cnpj && c.ie ? '  ' : ''}${c.ie ? `IE: ${c.ie}` : ''}`);
                          if (c.address) lines.push(`end: ${c.address}`);
                          if (c.neighborhood || c.phone) lines.push(`${c.neighborhood ? `bairro: ${c.neighborhood}` : ''}${c.neighborhood && c.phone ? '  ' : ''}${c.phone ? `f: ${c.phone}` : ''}`);
                          if (c.city || c.zip_code) {
                            const cityPart = c.city ? `cidade: ${c.city}${c.state ? `-${c.state}` : ''}` : '';
                            lines.push(`${cityPart}${c.city && c.zip_code ? '  ' : ''}${c.zip_code ? `cep: ${c.zip_code}` : ''}`);
                          }
                          set('receipt_header', lines.join('\n'));
                        }}
                        data-testid="receipt-header-autofill"
                      >
                        Preencher com dados da Empresa
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => set('receipt_header', '')}
                        className="text-red-600 border-red-300 hover:bg-red-50"
                        data-testid="receipt-header-clear"
                      >
                        Limpar
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs uppercase tracking-wider font-bold">Pré-visualização</Label>
                    <div className="bg-white border-2 border-dashed border-slate-300 rounded-md p-3 h-44 overflow-y-auto" data-testid="receipt-preview">
                      <div className="font-mono text-xs text-slate-900">
                        {(data.receipt_header && data.receipt_header.trim() ? data.receipt_header.split('\n') : [
                          data.trade_name || 'NOME DA EMPRESA',
                          (data.cnpj || data.ie) ? `${data.cnpj ? `cnpj: ${data.cnpj}` : ''}${data.cnpj && data.ie ? '  ' : ''}${data.ie ? `IE: ${data.ie}` : ''}` : '',
                          data.address ? `end: ${data.address}` : '',
                          (data.neighborhood || data.phone) ? `${data.neighborhood ? `bairro: ${data.neighborhood}` : ''}${data.neighborhood && data.phone ? '  ' : ''}${data.phone ? `f: ${data.phone}` : ''}` : '',
                          (data.city || data.zip_code) ? `${data.city ? `cidade: ${data.city}${data.state ? `-${data.state}` : ''}` : ''}${data.city && data.zip_code ? '  ' : ''}${data.zip_code ? `cep: ${data.zip_code}` : ''}` : '',
                        ].filter(Boolean)).map((line, i) => (
                          i === 0
                            ? <div key={i} className="text-center font-bold text-sm py-0.5">{line || '\u00A0'}</div>
                            : <div key={i} className="text-center text-[10px] py-0.5">{line || '\u00A0'}</div>
                        ))}
                        <div className="border-t border-dashed border-slate-400 my-1"></div>
                        <div className="text-center font-bold text-[10px]">cupom nao fiscal</div>
                        <div className="text-center text-[10px]">venda #28</div>
                        <div className="text-[10px]"><strong>operador:</strong> Administrador</div>
                        <div className="border-t border-dashed border-slate-400 my-1"></div>
                        <div className="text-[10px]">1 Tomate</div>
                        <div className="text-[10px] flex justify-between"><span>0.100 x R$ 10,98</span><span>R$ 1,10</span></div>
                      </div>
                    </div>
                    <p className="text-[10px] text-slate-500 mt-1">↑ assim ficará o topo do seu cupom</p>
                  </div>
                </div>

                <div className="mt-3 flex justify-end">
                  <Button onClick={handleSave} disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="settings-save-receipt-header">
                    <Save size={16} className="mr-2" /> {loading ? 'Salvando...' : 'Salvar Cabeçalho'}
                  </Button>
                </div>
              </div>

              {/* RODAPÉ DO CUPOM */}
              <div className="bg-white rounded-md border border-slate-200 p-5">
                <h3 className="font-bold text-slate-900 mb-2">Rodapé do Cupom</h3>
                <Input
                  value={data.receipt_footer || ''}
                  onChange={(e) => set('receipt_footer', e.target.value)}
                  placeholder="Ex: Volte sempre! Promoção quartas-feiras 10% off."
                  data-testid="settings-receipt-footer"
                />
                <p className="text-xs text-slate-500 mt-1">Aparece ANTES de &quot;obrigado pela preferência!&quot;</p>
              </div>

              {/* OPÇÃO PADRÃO DO MODAL DE CUPOM */}
              <div className="bg-white rounded-md border border-slate-200 p-5" data-testid="settings-cupom-default-box">
                <h3 className="font-bold text-slate-900 mb-1">Opção padrão ao finalizar a venda</h3>
                <p className="text-xs text-slate-500 mb-3">Qual botão já vem destacado no modal "Imprimir cupom?" — o operador só aperta Enter. Padrão: Não Imprimir.</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {[
                    { value: 'none', label: 'Não Imprimir', hint: 'Finaliza e já libera a próxima venda' },
                    { value: 'nao_fiscal', label: 'Imprimir Cupom Não Fiscal', hint: 'Cupom simples direto na impressora' },
                    { value: 'fiscal', label: 'Imprimir Cupom Fiscal', hint: 'NFC-e direto na impressora' },
                  ].map((o) => {
                    const active = (data.cupom_default_option || 'none') === o.value;
                    return (
                      <button
                        key={o.value}
                        type="button"
                        onClick={() => set('cupom_default_option', o.value)}
                        className={`text-left rounded-md border-2 p-3 transition-colors ${active ? 'border-blue-600 bg-blue-50' : 'border-slate-200 hover:border-blue-300'}`}
                        data-testid={`settings-cupom-default-${o.value}`}
                      >
                        <p className={`text-sm font-bold ${active ? 'text-blue-800' : 'text-slate-800'}`}>{active ? '● ' : '○ '}{o.label}</p>
                        <p className="text-[11px] text-slate-500 mt-0.5">{o.hint}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* IMPRESSORA TÉRMICA — MODO E REDE (ESC/POS) */}
              <div className="bg-white rounded-md border border-slate-200 p-5" data-testid="printer-network-box">
                <h3 className="font-bold text-slate-900 mb-1 flex items-center gap-2"><Printer size={18} /> Impressora térmica — como o cupom é enviado</h3>
                <p className="text-xs text-slate-500 mb-3">Impressão direta em ESC/POS. O sistema nunca abre a janela de impressão do navegador: sem agente/impressora, ele avisa com uma mensagem de erro.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                  {[
                    { value: 'agent', label: 'Agente no PC (USB) — padrão', hint: 'Smart Flow Print Agent instalado no caixa (porta 9123)' },
                    { value: 'network', label: 'Rede (IP/porta)', hint: 'Impressora Ethernet/Wi-Fi, ESC/POS pela porta 9100' },
                  ].map((o) => {
                    const mode = data.printer_mode === 'network' ? 'network' : 'agent';
                    const active = mode === o.value;
                    return (
                      <button key={o.value} type="button" onClick={() => set('printer_mode', o.value)}
                        className={`text-left rounded-md border-2 p-3 transition-colors ${active ? 'border-blue-600 bg-blue-50' : 'border-slate-200 hover:border-blue-300'}`}
                        data-testid={`printer-mode-${o.value}`}>
                        <p className={`text-sm font-bold ${active ? 'text-blue-800' : 'text-slate-800'}`}>{active ? '● ' : '○ '}{o.label}</p>
                        <p className="text-[11px] text-slate-500 mt-0.5">{o.hint}</p>
                      </button>
                    );
                  })}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                  <div className="md:col-span-2 space-y-1">
                    <Label className="text-xs">IP da impressora de rede</Label>
                    <Input value={data.printer_ip || ''} onChange={(e) => set('printer_ip', e.target.value.trim())} placeholder="Ex: 192.168.0.50" data-testid="printer-ip-input" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Porta</Label>
                    <Input type="number" value={data.printer_port ?? 9100} onChange={(e) => set('printer_port', Number(e.target.value) || 9100)} data-testid="printer-port-input" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Largura do papel</Label>
                    <select value={data.printer_columns || 48} onChange={(e) => set('printer_columns', Number(e.target.value))} className="w-full h-10 border border-slate-300 rounded-md px-2 text-sm bg-white" data-testid="printer-columns-select">
                      <option value={48}>80mm — 48 colunas</option>
                      <option value={42}>80mm — 42 colunas (Epson TM-T20 e similares)</option>
                      <option value={32}>58mm — 32 colunas</option>
                    </select>
                  </div>
                </div>
                <Alert className="bg-amber-50 border-amber-200 text-amber-800 mt-3" data-testid="printer-columns-hint">
                  <AlertDescription className="text-xs leading-relaxed">
                    <b>Texto cortado no cupom?</b> Se palavras quebram no fim da linha (ex.: "Di / nheiro") ou sobra um "R" solto à direita, a impressora tem menos colunas do que o configurado —
                    troque para <b>42 colunas</b> e imprima a régua de teste: a última linha deve mostrar a numeração inteira sem quebrar.
                  </AlertDescription>
                </Alert>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                  <label className={`flex items-start gap-3 rounded-md border-2 p-3 cursor-pointer ${data.printer_open_drawer ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200'}`} data-testid="printer-drawer-toggle">
                    <input type="checkbox" className="mt-1 w-4 h-4" checked={!!data.printer_open_drawer} onChange={(e) => set('printer_open_drawer', e.target.checked)} data-testid="printer-drawer-checkbox" />
                    <span>
                      <span className="block text-sm font-bold text-slate-900">Abrir gaveta de dinheiro nas vendas em dinheiro</span>
                      <span className="block text-[11px] text-slate-500 mt-0.5">Envia o comando ESC/POS (pino RJ11 da impressora) junto com o cupom — ou sozinho, se escolher "Não Imprimir". Ative só se tiver gaveta conectada à impressora.</span>
                    </span>
                  </label>
                  <label className={`flex items-start gap-3 rounded-md border-2 p-3 cursor-pointer ${data.printer_print_logo !== false ? 'border-blue-500 bg-blue-50' : 'border-slate-200'}`} data-testid="printer-logo-toggle">
                    <input type="checkbox" className="mt-1 w-4 h-4" checked={data.printer_print_logo !== false} onChange={(e) => set('printer_print_logo', e.target.checked)} data-testid="printer-logo-checkbox" />
                    <span>
                      <span className="block text-sm font-bold text-slate-900">Imprimir o logo da loja no topo do cupom</span>
                      <span className="block text-[11px] text-slate-500 mt-0.5">Usa o logo cadastrado na aba Empresa, convertido para preto e branco (impressora de rede).</span>
                    </span>
                  </label>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Button type="button" onClick={handleSave} disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="settings-save-printer">
                    <Save size={16} className="mr-2" /> {loading ? 'Salvando...' : 'Salvar Impressora'}
                  </Button>
                  <Button type="button" variant="outline" disabled={!data.printer_ip || netTest.busy} onClick={testNetworkPrinter} data-testid="printer-network-test-btn">
                    {netTest.busy ? 'Enviando…' : 'Testar impressora de rede'}
                  </Button>
                  {netTest.msg && <span className={`text-sm font-medium ${netTest.ok ? 'text-emerald-700' : 'text-red-700'}`} data-testid="printer-network-test-msg">{netTest.msg}</span>}
                  <span className="text-[11px] text-slate-500">Modo, IP/porta, largura do papel, gaveta e logo só valem no PDV depois de <b>Salvar Impressora</b>.</span>
                </div>
              </div>

              {/* IMPRESSÃO SILENCIOSA — AGENTE LOCAL (USB) */}
              <PrintAgentSection />
            </div>
          </TabsContent>

          {/* BACKUP */}
          <TabsContent value="backup">
            <BackupTab />
          </TabsContent>
          <TabsContent value="xml" className="space-y-4">
            <FiscalXmlExportPanel />
            <AccountantEmailPanel />
          </TabsContent>
        </Tabs>

        {error && <div className="mt-4"><Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert></div>}
        {success && <div className="mt-4"><Alert className="border-green-600 bg-green-50"><AlertDescription className="text-green-700 font-medium">{success}</AlertDescription></Alert></div>}
      </div>
    </Layout>
  );
};

/* ============ IMPRESSÃO SILENCIOSA — SMART FLOW PRINT AGENT (aba Impressora) ============ */
const PrintAgentSection = () => {
  const [status, setStatus] = useState(agentKnownOnline() ? 'online' : 'checking'); // checking | online | offline
  const [info, setInfo] = useState(agentInfo());
  const [testMsg, setTestMsg] = useState('');
  const check = async () => {
    if (!agentKnownOnline()) setStatus('checking');
    const ok = await probeAgent(true);
    setInfo(agentInfo());
    setStatus(ok ? 'online' : 'offline');
  };
  useEffect(() => { check(); }, []);
  const testPrint = async () => {
    setTestMsg('');
    try {
      const sep = '-'.repeat(48);
      const r = await printViaAgent(`        SMART FLOW - TESTE DE IMPRESSAO\n${sep}\nSe voce esta lendo isto, a impressao\nsilenciosa esta funcionando!\n${new Date().toLocaleString('pt-BR')}\n${sep}`);
      setTestMsg(`✓ Enviado para "${r.printer}"`);
    } catch (e) {
      setTestMsg(`Falha: ${e.message}`);
    }
  };
  const testColumns = async () => {
    setTestMsg('');
    try {
      const cfg = await loadCompanySettings();
      const r = await printViaAgent(columnsTestText(Number(cfg?.printer_columns) || 48));
      setTestMsg(`✓ Régua enviada para "${r.printer}" — veja qual linha numerada NÃO quebrou e escolha essa largura acima.`);
    } catch (e) {
      setTestMsg(`Falha: ${e.message}`);
    }
  };
  const testQr = async () => {
    setTestMsg('');
    try {
      const sep = '-'.repeat(48);
      const sample = 'http://www.dfe.ms.gov.br/nfce/qrcode?p=' + '5'.repeat(44) + '|2|1|1|' + 'A'.repeat(40);
      const text = `        SMART FLOW - TESTE DE QR CODE\n${sep}\nAbaixo deve sair um QR Code do tamanho\nde uma NFC-e real (${sample.length} caracteres).\n${sep}\n[[QR:${encodeURIComponent(sample)}]]\nSe o QR saiu e o cupom cortou: OK!\n${sep}`;
      const r = await printViaAgent(inlineQrCodes(text));
      setTestMsg(`✓ QR de teste enviado para "${r.printer}" (modo ${agentInfo()?.raw_b64 ? 'bytes brutos' : 'compatível'})`);
    } catch (e) {
      setTestMsg(`Falha: ${e.message}`);
    }
  };
  const outdated = status === 'online' && !info?.raw_b64;
  const dl = (name) => `${API}/print-agent/files/${name}?origin=${encodeURIComponent(window.location.origin)}`;
  return (
    <div className="bg-white rounded-md border border-slate-200 p-5" data-testid="print-agent-section">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2"><Printer size={18} /> Impressão silenciosa (sem janela do navegador)</h2>
          <p className="text-xs text-slate-500 mt-1">O <b>Smart Flow Print Agent</b> roda no PC do caixa e envia o cupom direto para a impressora térmica — sem Ctrl+P, sem prévia, sem confirmar nada.</p>
        </div>
        <div className={`px-3 py-1.5 rounded-md text-sm font-bold border ${status === 'online' ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : status === 'offline' ? 'bg-red-50 border-red-300 text-red-700' : 'bg-slate-50 border-slate-300 text-slate-600'}`} data-testid="print-agent-status">
          {status === 'online' ? `● Agente ativo · ${info?.printer || 'impressora padrão'}` : status === 'offline' ? '○ Agente não encontrado neste PC' : '… verificando'}
        </div>
      </div>

      {status === 'online' ? (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button type="button" onClick={testPrint} className="bg-emerald-600 hover:bg-emerald-700" data-testid="print-agent-test-btn">Testar impressão</Button>
          <Button type="button" variant="outline" onClick={testColumns} data-testid="print-agent-columns-test-btn">Testar largura (régua)</Button>
          <Button type="button" variant="outline" onClick={testQr} data-testid="print-agent-qr-test-btn">Testar QR Code</Button>
          <Button type="button" variant="outline" onClick={check} data-testid="print-agent-recheck-btn">Verificar novamente</Button>
          {testMsg && <span className="text-sm font-medium" data-testid="print-agent-test-msg">{testMsg}</span>}
          <p className="w-full text-[11px] text-slate-500" data-testid="print-agent-version">Versão do agente: {info?.version || '?'}{info?.raw_b64 ? ' · QR Code em bytes brutos (recomendado)' : ''}</p>
          {outdated && (
            <div className="w-full rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="print-agent-outdated">
              <b>Atualização recomendada (v1.3):</b> a versão nova envia o QR Code da NFC-e em bytes brutos, eliminando qualquer risco de a impressora travar no QR.
              Baixe o <a href={dl('SmartFlowPrintAgent.ps1')} download className="underline font-bold">SmartFlowPrintAgent.ps1</a> e o <a href={dl('Instalar-PrintAgent.bat')} download className="underline font-bold">Instalar-PrintAgent.bat</a> para a mesma pasta e rode o .bat de novo.
            </div>
          )}
          {info?.printers?.length > 1 && (
            <p className="w-full text-[11px] text-slate-500">Impressoras no PC: {info.printers.join(' · ')}. Para trocar, crie <code>%LOCALAPPDATA%\SmartFlow\impressora.txt</code> com o nome exato.</p>
          )}
        </div>
      ) : (
        <div className="mt-4 border-2 border-emerald-300 rounded-md p-4 bg-emerald-50">
          <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="print-agent-chrome-hint">
            <b>Já instalou o agente e ainda aparece "não encontrado"?</b> O Chrome/Edge (versão 142+) bloqueia o acesso ao PC local até você permitir:
            clique no ícone à esquerda do endereço do site → ative <b>"Apps no dispositivo"</b> (ou "Rede local") → clique em <b>Verificar novamente</b>.
            Se aparecer a pergunta de permissão do Chrome, clique em <b>Permitir</b>. O instalador abaixo (versão nova) já libera isso automaticamente — basta rodar de novo.
          </div>
          <p className="font-bold text-emerald-900 mb-2">Instalar no PC do caixa (Windows) — 1 minuto</p>
          <ol className="list-decimal pl-5 space-y-2 text-sm text-emerald-900">
            <li>Baixe os dois arquivos para a <b>mesma pasta</b> (ex.: Downloads):
              <div className="flex flex-wrap gap-2 mt-2">
                <a href={dl('SmartFlowPrintAgent.ps1')} download className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-white border border-emerald-400 text-emerald-800 text-sm font-bold hover:bg-emerald-100" data-testid="print-agent-dl-ps1"><Download size={14} /> SmartFlowPrintAgent.ps1</a>
                <a href={dl('Instalar-PrintAgent.bat')} download className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-white border border-emerald-400 text-emerald-800 text-sm font-bold hover:bg-emerald-100" data-testid="print-agent-dl-bat"><Download size={14} /> Instalar-PrintAgent.bat</a>
                <a href={dl('Ativar-Inicio-Automatico.bat')} download className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-white border border-emerald-400 text-emerald-800 text-sm font-bold hover:bg-emerald-100" title="Faz o agente abrir sozinho (oculto) com o Windows: pasta Inicialização + tarefa agendada" data-testid="print-agent-dl-autostart"><Download size={14} /> Ativar-Inicio-Automatico.bat</a>
              </div>
            </li>
            <li>Dê <b>dois cliques em Instalar-PrintAgent.bat</b>. Ele instala, inicia agora, passa a abrir junto com o Windows e autoriza o Chrome/Edge a acessar o agente (feche e reabra o navegador depois).</li>
            <li><b>Não abre sozinho ao ligar o PC?</b> Baixe e dê dois cliques em <b>Ativar-Inicio-Automatico.bat</b> (na mesma pasta do .ps1): ele cria o atalho oculto na pasta <i>Inicialização</i> e uma <i>Tarefa Agendada</i> "SmartFlowPrintAgent" ao fazer logon — o agente passa a iniciar em segundo plano, sem janela, e o .bat mostra "SIM" em cada etapa.</li>
            <li>Deixe a impressora térmica como <b>impressora padrão</b> do Windows (ou crie o arquivo <code>impressora.txt</code> com o nome dela).</li>
            <li>Volte aqui e clique em <b>Verificar novamente</b> → "Agente ativo" → <b>Testar impressão</b>.</li>
          </ol>
          <div className="mt-3 flex items-center gap-2">
            <Button type="button" variant="outline" onClick={check} data-testid="print-agent-recheck-btn">Verificar novamente</Button>
            <span className="text-[11px] text-slate-600">Sem o agente, nenhuma impressão é feita — o sistema mostra uma mensagem de erro (nunca abre a janela do navegador).</span>
          </div>
        </div>
      )}
    </div>
  );
};

/* ============ LOGO DA EMPRESA (aba Empresa) ============ */
const CompanyLogoField = ({ logo, onChanged, setError, setSuccess }) => {
  const [busy, setBusy] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      setError('A logo deve ser PNG ou JPG.');
      return;
    }
    if (file.size > 1_500_000) {
      setError('Logo muito grande. Envie uma imagem de até 1,5 MB.');
      return;
    }
    setBusy(true); setError(''); setSuccess('');
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const r = await axios.put(`${API}/settings/company/logo`, { logo_base64: dataUrl }, { withCredentials: true });
      onChanged(r.data);
      await refreshCompanySettings();
      setSuccess('Logo atualizada com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao enviar a logo');
    } finally { setBusy(false); }
  };

  const removeLogo = async () => {
    setBusy(true); setError(''); setSuccess('');
    try {
      const r = await axios.delete(`${API}/settings/company/logo`, { withCredentials: true });
      onChanged(r.data);
      await refreshCompanySettings();
      setSuccess('Logo removida.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao remover a logo');
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-md border border-slate-200 p-6 mb-4" data-testid="company-logo-field">
      <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2 mb-1">
        <ImageIcon size={18} className="text-blue-600" /> Logo da Empresa
      </h2>
      <p className="text-sm text-slate-600 mb-4">
        Envie a logo em <strong>PNG</strong> ou <strong>JPG</strong> (até 1,5 MB). Ela aparece no PDV ao lado do nome da empresa.
        Sem logo, o visual padrão do Smart Flow é mantido.
      </p>
      <div className="flex items-center gap-5 flex-wrap">
        <div className="w-32 h-32 rounded-md border-2 border-dashed border-slate-300 flex items-center justify-center bg-slate-50 overflow-hidden shrink-0" data-testid="logo-preview">
          {logo
            ? <img src={logo} alt="Logo da empresa" className="max-w-full max-h-full object-contain" data-testid="logo-preview-img" />
            : <span className="text-xs text-slate-400 text-center px-2">Sem logo<br />(padrão Smart Flow)</span>}
        </div>
        <div className="flex flex-col gap-2">
          <label>
            <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={handleFile} disabled={busy} data-testid="logo-file-input" />
            <span className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-bold cursor-pointer ${busy ? 'bg-slate-300 text-slate-500' : 'bg-blue-600 hover:bg-blue-700 text-white'}`} data-testid="logo-upload-btn">
              <Upload size={16} className="mr-2" /> {busy ? 'Enviando...' : (logo ? 'Trocar logo' : 'Enviar logo')}
            </span>
          </label>
          {logo && (
            <Button type="button" variant="outline" onClick={removeLogo} disabled={busy} className="text-red-600 border-red-300 hover:bg-red-50" data-testid="logo-remove-btn">
              <Trash2 size={16} className="mr-2" /> Remover logo
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ============ BACKUP TAB ============ */
const BackupTab = () => {
  const [info, setInfo] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [mode, setMode] = useState('merge');
  const [confirmText, setConfirmText] = useState('');
  const [file, setFile] = useState(null);

  const loadInfo = async () => {
    try {
      const { data } = await axios.get(`${API}/backup/info`, { withCredentials: true });
      setInfo(data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Erro ao carregar informações');
    }
  };

  useEffect(() => { loadInfo(); }, []);

  const handleExport = async () => {
    setExporting(true); setMsg(''); setErr('');
    try {
      const response = await axios.get(`${API}/backup/export`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
      a.href = url;
      a.download = `smartflow_backup_${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setMsg('Backup baixado com sucesso!');
      setTimeout(() => setMsg(''), 4000);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Erro ao gerar backup');
    } finally { setExporting(false); }
  };

  const handleRestore = async () => {
    if (!file) { setErr('Selecione um arquivo de backup'); return; }
    if (mode === 'replace' && confirmText !== 'SUBSTITUIR') {
      setErr('Para substituir o banco, digite SUBSTITUIR no campo de confirmação.');
      return;
    }
    setRestoring(true); setMsg(''); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/backup/restore?mode=${mode}`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const lines = Object.entries(data.summary || {}).map(([k, v]) => `${k}: +${v.inserted} novos, ~${v.updated} atualizados`);
      setMsg('Restore concluído! ' + lines.slice(0, 4).join(' | '));
      setFile(null);
      setConfirmText('');
      await loadInfo();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Erro ao restaurar backup');
    } finally { setRestoring(false); }
  };

  return (
    <div className="space-y-6" data-testid="backup-tab">
      {/* Atualização do Sistema */}
      <SystemUpdateSection />

      {/* Header */}
      <div className="bg-white rounded-md border border-slate-200 p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-12 h-12 rounded-md bg-emerald-600 flex items-center justify-center shrink-0">
            <Database className="text-white" size={24} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">Backup & Restauração</h2>
            <p className="text-sm text-slate-600">Baixe todos os seus dados num único arquivo JSON. Útil para segurança, migração ou auditoria.</p>
          </div>
        </div>

        {info && (
          <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">Resumo do banco</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
              {info.collections.map((c) => (
                <div key={c.name} className="bg-white rounded border border-slate-200 px-3 py-2 flex justify-between" data-testid={`backup-info-${c.name}`}>
                  <span className="text-slate-600 capitalize">{c.name.replace(/_/g, ' ')}</span>
                  <span className="font-bold text-slate-900">{c.count}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-3">Total: <strong>{info.total_documents}</strong> documentos · Banco: <code className="bg-slate-200 px-1 rounded">{info.db_name}</code></p>
          </div>
        )}
      </div>

      {/* Exportar */}
      <div className="bg-white rounded-md border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-3">
          <Download className="text-blue-600" size={20} />
          <h3 className="font-bold text-slate-900 text-lg">Exportar Backup</h3>
        </div>
        <p className="text-sm text-slate-600 mb-4">Baixa <strong>todos</strong> os dados (produtos, vendas, clientes, caixa, fluxo, fornecedores, configurações, usuários) em um arquivo <code className="bg-slate-100 px-1 rounded">.json</code>. Guarde em local seguro.</p>
        <Button
          onClick={handleExport}
          disabled={exporting}
          className="bg-blue-600 hover:bg-blue-700 text-white"
          data-testid="backup-export-btn"
        >
          <Download size={16} className="mr-2" />
          {exporting ? 'Gerando backup...' : 'Baixar Backup Agora'}
        </Button>
      </div>

      {/* Restaurar */}
      <div className="bg-white rounded-md border border-amber-300 p-6">
        <div className="flex items-center gap-3 mb-3">
          <Upload className="text-amber-600" size={20} />
          <h3 className="font-bold text-slate-900 text-lg">Restaurar Backup</h3>
        </div>
        <p className="text-sm text-slate-600 mb-4">
          Importa um arquivo de backup gerado anteriormente. Use com cuidado.
        </p>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Arquivo de backup (.json)</Label>
            <Input
              type="file"
              accept=".json,application/json"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="backup-file-input"
            />
          </div>

          <div className="space-y-1">
            <Label>Modo</Label>
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setMode('merge')}
                className={`px-4 py-2 rounded-md text-sm font-bold border ${mode === 'merge' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-700 border-slate-300'}`}
                data-testid="backup-mode-merge"
              >
                Mesclar (recomendado)
              </button>
              <button
                type="button"
                onClick={() => setMode('replace')}
                className={`px-4 py-2 rounded-md text-sm font-bold border ${mode === 'replace' ? 'bg-red-600 text-white border-red-600' : 'bg-white text-slate-700 border-slate-300'}`}
                data-testid="backup-mode-replace"
              >
                Substituir tudo (perigoso)
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              <strong>Mesclar</strong>: insere/atualiza por ID, preserva dados extras. <strong>Substituir</strong>: apaga as collections existentes antes de importar.
            </p>
          </div>

          {mode === 'replace' && (
            <div className="bg-red-50 border border-red-300 rounded-md p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="text-red-600 shrink-0 mt-0.5" size={18} />
                <div className="flex-1">
                  <p className="text-sm font-bold text-red-800 mb-1">Atenção! Esta operação apaga todos os dados atuais.</p>
                  <p className="text-xs text-red-700 mb-2">Digite <strong>SUBSTITUIR</strong> para confirmar:</p>
                  <Input
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="SUBSTITUIR"
                    data-testid="backup-confirm-text"
                    className="bg-white"
                  />
                </div>
              </div>
            </div>
          )}

          <Button
            onClick={handleRestore}
            disabled={restoring || !file}
            className={mode === 'replace' ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
            data-testid="backup-restore-btn"
          >
            <Upload size={16} className="mr-2" />
            {restoring ? 'Restaurando...' : (mode === 'replace' ? 'Substituir Banco com Backup' : 'Mesclar Backup no Banco')}
          </Button>
        </div>
      </div>

      {msg && <Alert className="border-green-600 bg-green-50"><AlertDescription className="text-green-700 font-medium">{msg}</AlertDescription></Alert>}
      {err && <Alert variant="destructive"><AlertDescription>{err}</AlertDescription></Alert>}
    </div>
  );
};


/* ============ SYSTEM UPDATE SECTION (dentro da aba Backup) ============ */
const SystemUpdateSection = () => {
  const { status, loading, forceCheck, approve, cancelApproval, refresh } = useUpdateStatus();
  const [pwd, setPwd] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => { refresh(); }, [refresh]);

  const showBanner = (kind, text) => {
    if (kind === 'ok') { setMsg(text); setErr(''); setTimeout(() => setMsg(''), 4000); }
    else { setErr(text); setMsg(''); setTimeout(() => setErr(''), 5000); }
  };

  const doCheck = async () => {
    const r = await forceCheck();
    if (r?.ok) showBanner('ok', 'Verificação concluída');
    else showBanner('err', r?.error || 'Falha ao verificar');
  };

  const doApprove = async (e) => {
    e.preventDefault();
    if (!pwd) return;
    const r = await approve(pwd);
    if (r?.ok) { showBanner('ok', 'Atualização aprovada — executará em até 1 hora.'); setPwd(''); }
    else showBanner('err', r?.error || 'Falha ao aprovar');
  };

  const doCancel = async () => {
    const r = await cancelApproval();
    if (r?.ok) showBanner('ok', 'Aprovação cancelada');
    else showBanner('err', r?.error || 'Falha ao cancelar');
  };

  if (!status && loading) {
    return (
      <div className="bg-white rounded-md border border-slate-200 p-6" data-testid="system-update-loading">
        <p className="text-sm text-slate-500">Carregando status de atualização...</p>
      </div>
    );
  }
  if (!status) return null;

  const pending = status.pending_approval;
  const available = status.update_available;

  return (
    <div className="bg-white rounded-md border border-slate-200 p-6" data-testid="system-update-section">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className={`w-12 h-12 rounded-md flex items-center justify-center shrink-0 ${pending ? 'bg-amber-500' : available ? 'bg-blue-600' : 'bg-emerald-600'}`}>
            {pending ? <AlertTriangle className="text-white" size={24} /> :
              available ? <RefreshCw className="text-white" size={24} /> :
              <CheckCircle2 className="text-white" size={24} />}
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">Atualização do Sistema</h2>
            <p className="text-sm text-slate-600">
              {pending ? 'Atualização aprovada — aguardando o agendador executar (até 1h).' :
                available ? 'Uma nova versão está disponível para instalação.' :
                'Sistema em dia. Nenhuma atualização pendente.'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`px-3 py-1 rounded text-[11px] font-black uppercase tracking-wider ${pending ? 'bg-amber-500 text-white' :
              available ? 'bg-blue-600 text-white' :
              'bg-emerald-600 text-white'}`}
            data-testid="update-badge"
          >
            {pending ? 'AGUARDANDO EXECUÇÃO' : available ? 'NOVA VERSÃO' : 'EM DIA'}
          </span>
          <Button variant="outline" size="sm" onClick={doCheck} disabled={loading} data-testid="update-force-check-settings">
            <RefreshCw size={14} className="mr-1" /> Verificar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm bg-slate-50 border border-slate-200 rounded-md p-3">
        <div>
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Instalada</p>
          <p className="font-mono text-slate-900 font-bold" data-testid="update-current-version">{status.current_version}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Disponível</p>
          <p className={`font-bold ${available ? 'text-blue-600' : 'text-slate-800'}`} data-testid="update-latest-version">
            {status.latest_version || '—'}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Última verificação</p>
          <p className="text-slate-700 text-xs">{status.last_check_at ? new Date(status.last_check_at).toLocaleString('pt-BR') : 'Nunca'}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Canal</p>
          <p className="capitalize text-slate-700">{status.channel}</p>
        </div>
      </div>

      {status.changelog && (
        <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded text-xs whitespace-pre-wrap max-h-40 overflow-y-auto" data-testid="update-changelog-settings">
          <div className="font-bold text-slate-700 mb-1 uppercase tracking-wider text-[10px]">Novidades</div>
          {status.changelog}
        </div>
      )}

      {available && !pending && (
        <form onSubmit={doApprove} className="mt-4 space-y-2" data-testid="update-approve-form-settings">
          <div className="bg-blue-50 border border-blue-300 rounded p-3 text-xs text-blue-900">
            <strong>Antes de aprovar:</strong> a atualização executa <em>backup automático</em>, faz <em>rebuild</em> do sistema e reinicia os serviços.
            O processo é iniciado pelo Windows Task Scheduler em até 1 hora após a aprovação. Confirme com sua senha de admin.
          </div>
          <Label>Senha do administrador</Label>
          <Input
            type="password"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            data-testid="update-password-settings"
            required
          />
          <Button type="submit" disabled={loading || !pwd} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="update-approve-btn-settings">
            <RefreshCw size={16} className="mr-2" /> {loading ? 'Enviando...' : `Aprovar Atualização para ${status.latest_version}`}
          </Button>
        </form>
      )}

      {pending && (
        <div className="mt-4 space-y-2">
          <div className="bg-amber-50 border border-amber-300 rounded p-3 text-xs text-amber-900">
            <strong>Aguardando execução</strong> — aprovada por <strong>{status.approved_by}</strong>.
            Executará automaticamente na próxima janela do agendador (até 1 hora).
            Cancele agora se precisar reverter — após rodar não tem volta.
          </div>
          <Button variant="outline" onClick={doCancel} disabled={loading} data-testid="update-cancel-btn-settings">
            Cancelar aprovação
          </Button>
        </div>
      )}

      {!available && !pending && (
        <p className="mt-3 text-xs text-slate-500">
          Você receberá aqui um aviso quando uma nova versão do Smart Flow for publicada.
        </p>
      )}

      {msg && <Alert className="mt-3 border-green-600 bg-green-50"><AlertDescription className="text-green-700 font-medium" data-testid="update-msg-ok">{msg}</AlertDescription></Alert>}
      {err && <Alert className="mt-3" variant="destructive"><AlertDescription data-testid="update-msg-err">{err}</AlertDescription></Alert>}
    </div>
  );
};
